'use client';

import posthog from 'posthog-js';

type ProductEvent =
  | 'widget_loaded'
  | 'tracking_search_started'
  | 'tracking_search_failed'
  | 'tracking_result_viewed'
  | 'shipment_save_signup_prompted'
  | 'shipment_save_limit_reached'
  | 'shipment_saved'
  | 'sign_up'
  | 'profile_claimed'
  | 'account_login'
  | 'account_login_failed'
  | 'confirmation_email_resent'
  | 'begin_checkout'
  | 'purchase';

// Never send references, customer names, notes, routes or raw API errors.
type ProductProperties = {
  surface?: 'site' | 'widget';
  partner_slug?: string;
  result_kind?: 'live' | 'archive' | 'paywall' | 'empty' | 'unavailable';
  http_status?: number;
  limit?: number;
  save_source?: 'manual' | 'after_signup';
  account_type?: 'shipper' | 'forwarder';
  method?: 'email';
  transaction_id?: string;
  value?: number;
  currency?: string;
};

export function recordProductEvent(name: ProductEvent, properties: ProductProperties = {}, destinations: { posthog?: boolean } = {}) {
  if (typeof window === 'undefined' || process.env.NODE_ENV !== 'production') return;
  // Analytics must never turn a successful product action into a UI failure.
  try { if (destinations.posthog !== false) posthog.capture(name, properties); } catch { /* best effort */ }
  try { window.gtag?.('event', name, properties); } catch { /* best effort */ }
}

export function identifyProductUser(id: string, accountType: 'shipper' | 'forwarder') {
  try { posthog.identify(id, { account_type: accountType }); } catch { /* best effort */ }
}

/** Call only with the response from the authenticated checkout verification API. */
export function recordVerifiedPurchase(data: { accountType: 'shipper' | 'forwarder'; payment?: {
  transaction_id: string; value: number; currency: string; live: boolean;
} | null }) {
  const payment = data.payment;
  if (!payment?.live || !payment.transaction_id || payment.value <= 0) return;
  const key = `shipzy-purchase:${payment.transaction_id}`;
  try { if (sessionStorage.getItem(key)) return; } catch { /* storage may be disabled */ }
  // Stripe's invoice.paid webhook owns PostHog payment_confirmed. This browser
  // event fills GA4's purchase step without duplicating the server event.
  recordProductEvent('purchase', { transaction_id: payment.transaction_id, value: payment.value,
    currency: payment.currency, account_type: data.accountType }, { posthog: false });
  try { sessionStorage.setItem(key, '1'); } catch { /* best effort */ }
}
